import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import axios from "axios";
import React, { Suspense } from "react";

// Configure axios to include auth token from localStorage
axios.interceptors.request.use((config) => {
  const token = localStorage.getItem('session_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Kiosk Pages
import KioskWelcome from "./pages/kiosk/KioskWelcome";
import KioskAttract from "./pages/kiosk/KioskAttract";
import KioskCategory from "./pages/kiosk/KioskCategory";
import KioskForm from "./pages/kiosk/KioskForm";
import KioskBadgeCapture from "./pages/kiosk/KioskBadgeCapture";
import KioskBlueBadgeIntro from "./pages/kiosk/KioskBlueBadgeIntro";
import KioskBadgeScan from "./pages/kiosk/KioskBadgeScan";
import KioskBadgeReview from "./pages/kiosk/KioskBadgeReview";
import KioskProcessing from "./pages/kiosk/KioskProcessing";
import KioskConfirmation from "./pages/kiosk/KioskConfirmation";
import KioskLookup from "./pages/kiosk/KioskLookup";
import KioskStatus from "./pages/kiosk/KioskStatus";
import KioskPayChoice from "./pages/kiosk/KioskPayChoice";
import KioskPayQREntry from "./pages/kiosk/KioskPayQREntry";
import KioskPayQR from "./pages/kiosk/KioskPayQR";
import KioskPayKiosk from "./pages/kiosk/KioskPayKiosk";
import KioskSessionPayment from "./pages/kiosk/KioskSessionPayment";
import KioskSessionLookup from "./pages/kiosk/KioskSessionLookup";
import TracyTest from "./pages/kiosk/TracyTest";
import KioskSetup from "./pages/kiosk/KioskSetup";

// Admin Pages
import AdminLogin from "./pages/admin/AdminLogin";
import AdminDashboard from "./pages/admin/AdminDashboard";
import AdminRegistrations from "./pages/admin/AdminRegistrations";
import AdminRegistrationDetail from "./pages/admin/AdminRegistrationDetail";
import AdminOCRExceptions from "./pages/admin/AdminOCRExceptions";
import AdminAuditLog from "./pages/admin/AdminAuditLog";
import AdminSites from "./pages/admin/AdminSites";
import AdminRetailers from "./pages/admin/AdminRetailers";
import AdminRewards from "./pages/admin/AdminRewards";
import AdminAnalytics from "./pages/admin/AdminAnalytics";
import AuthCallback from "./pages/admin/AuthCallback";
// Phase 4 - Signage CMS
import AdminScreens from "./pages/admin/AdminScreens";
import AdminContent from "./pages/admin/AdminContent";
import AdminPlaylists from "./pages/admin/AdminPlaylists";
// Phase 5 - Revenue Modules
import AdminAdvertisers from "./pages/admin/AdminAdvertisers";
import AdminCampaigns from "./pages/admin/AdminCampaigns";
import AdminBilling from "./pages/admin/AdminBilling";
import AdminRevenue from "./pages/admin/AdminRevenue";
import AdminSettings from "./pages/admin/AdminSettings";
import AdminEntitlements from "./pages/admin/AdminEntitlements";
import AdminEntitlementsV2 from "./pages/admin/AdminEntitlementsV2";
import AdminReceipts from "./pages/admin/AdminReceipts";
import AdminSystemStatus from "./pages/admin/AdminSystemStatus";
import AdminFreeflow from "./pages/admin/AdminFreeflow";
import AdminExitAuths from "./pages/admin/AdminExitAuths";
import AdminBarrierControl from "./pages/admin/AdminBarrierControl";
import AdminLiveSessions from "./pages/admin/AdminLiveSessions";
import AdminSpaceManagement from "./pages/admin/AdminSpaceManagement";
import AdminCameras from "./pages/admin/AdminCameras";
import AdminCCTV from "./pages/admin/AdminCCTV";
import AdminDailyMovement from "./pages/admin/AdminDailyMovement";
import AdminVehicleHistory from "./pages/admin/AdminVehicleHistory";
// Stripe Terminal Management - Lazy loaded to prevent bundle issues
const AdminKiosks = React.lazy(() => import("./pages/admin/AdminKiosks"));
const AdminReaders = React.lazy(() => import("./pages/admin/AdminReaders"));
// Kyle Rise Payments & Passes (Legacy - Phase 2)
import {
  KyleRiseDashboard,
  KyleRiseRetailers,
  KyleRiseRetailerDetail,
  KyleRiseStaffPasses,
  KyleRiseResidentPasses,
  KyleRisePayments,
  KyleRiseInterests,
  KyleRiseEmailLogs,
  KyleRiseQRManager,
  KyleRiseReports
} from "./pages/admin/kyle-rise";
// Commercial Management Suite (Phase 3A - Multi-site)
import {
  CommercialSites,
  CommercialPayments,
  CommercialPaymentDetail,
  CommercialQRCentre,
  CommercialRetailers,
  CommercialRetailerDetail,
  CommercialResidentPasses,
  CommercialStaffPasses,
  CommercialSeasonTickets,
  CommercialLongTerm,
  CommercialInterests,
  CommercialFinance,
  CommercialReports,
  CommercialEmailCentre,
  CommercialAuditCentre,
  CommercialSettings,
  CommercialProductsTariffs
} from "./pages/admin/commercial";

// Mobile Operations Centre (Phase P1)
import MobileHome from "./pages/mobile/MobileHome";
import MobileBarriers from "./pages/mobile/MobileBarriers";
import MobileActivity from "./pages/mobile/MobileActivity";
import MobileCameras from "./pages/mobile/MobileCameras";
import MobileReports from "./pages/mobile/MobileReports";
// Test Control Panel
import TestControlPanel from "./pages/admin/TestControlPanel";
// Advertising Management Module
import AdvertisingDashboard from "./pages/admin/advertising/AdvertisingDashboard";
import AdvertisingAdvertisers from "./pages/admin/advertising/AdvertisingAdvertisers";
import AdvertisingScreens from "./pages/admin/advertising/AdvertisingScreens";
import AdvertisingCampaigns from "./pages/admin/advertising/AdvertisingCampaigns";
import AdvertisingCalendar from "./pages/admin/advertising/AdvertisingCalendar";
import AdvertisingApprovals from "./pages/admin/advertising/AdvertisingApprovals";
import AdvertisingProofOfPlay from "./pages/admin/advertising/AdvertisingProofOfPlay";
import AdvertisingBilling from "./pages/admin/advertising/AdvertisingBilling";
// Advertiser Self-Service Portal
import AdvertiserLogin from "./pages/advertiser/AdvertiserLogin";
import AdvertiserDashboard from "./pages/advertiser/AdvertiserDashboard";
import AdvertiserCampaigns from "./pages/advertiser/AdvertiserCampaigns";
import CreateCampaign from "./pages/advertiser/CreateCampaign";
import CampaignDetail from "./pages/advertiser/CampaignDetail";
import AdvertiserBilling from "./pages/advertiser/AdvertiserBilling";

// Visit Pages (Mobile)
import VisitCapture from "./pages/visit/VisitCapture";
import VisitSuccess from "./pages/visit/VisitSuccess";

// Screen Player (Signage)
import ScreenPlayer from "./pages/player/ScreenPlayer";

// Mobile Payment (Wall QR)
import MobilePay from "./pages/mobile/MobilePay";

// Mobile Operations Platform - Barrier Control Only
import MobileOpsControl from "./pages/mobile/MobileOpsControl";

// Radisson Benefit QR Pages (R9.94)
import RadissonRestaurant from "./pages/radisson/RadissonRestaurant";
import RadissonSpa from "./pages/radisson/RadissonSpa";
// Radisson Payment Page (R11.20)
import RadissonPay from "./pages/radisson/RadissonPay";

import "@/App.css";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
function AppRouter() {
  const location = useLocation();
  
  // Check URL fragment for session_id (OAuth callback)
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  
  return (
    <Routes>
      {/* Landing redirect */}
      <Route path="/" element={<Navigate to="/kiosk" replace />} />
      
      {/* Kiosk Routes */}
      <Route path="/kiosk" element={<KioskWelcome />} />
      <Route path="/kiosk/setup" element={<KioskSetup />} />
      <Route path="/kiosk/tracy-test" element={<TracyTest />} />
      <Route path="/kiosk/attract" element={<KioskAttract />} />
      <Route path="/kiosk/lookup" element={<KioskLookup />} />
      <Route path="/kiosk/status" element={<KioskStatus />} />
      <Route path="/kiosk/category" element={<KioskCategory />} />
      <Route path="/kiosk/form" element={<KioskForm />} />
      <Route path="/kiosk/badge-capture" element={<KioskBadgeCapture />} />
      <Route path="/kiosk/badge-intro" element={<KioskBlueBadgeIntro />} />
      <Route path="/kiosk/badge-scan" element={<KioskBadgeScan />} />
      <Route path="/kiosk/badge-review" element={<KioskBadgeReview />} />
      <Route path="/kiosk/processing" element={<KioskProcessing />} />
      <Route path="/kiosk/confirmation" element={<KioskConfirmation />} />
      <Route path="/kiosk/pay" element={<KioskPayKiosk />} />
      <Route path="/kiosk/pay-qr-entry" element={<KioskPayQREntry />} />
      <Route path="/kiosk/pay-qr" element={<KioskPayQR />} />
      <Route path="/kiosk/pay-kiosk" element={<KioskPayKiosk />} />
      <Route path="/kiosk/session-lookup" element={<KioskSessionLookup />} />
      <Route path="/kiosk/session-payment" element={<KioskSessionPayment />} />
      
      {/* Admin Routes */}
      <Route path="/admin/login" element={<AdminLogin />} />
      <Route path="/admin" element={<AdminDashboard />} />
      <Route path="/admin/dashboard" element={<AdminDashboard />} />
      <Route path="/admin/registrations" element={<AdminRegistrations />} />
      <Route path="/admin/registrations/:id" element={<AdminRegistrationDetail />} />
      <Route path="/admin/sites" element={<AdminSites />} />
      <Route path="/admin/retailers" element={<AdminRetailers />} />
      <Route path="/admin/rewards" element={<AdminRewards />} />
      <Route path="/admin/analytics" element={<AdminAnalytics />} />
      <Route path="/admin/ocr-exceptions" element={<AdminOCRExceptions />} />
      <Route path="/admin/audit-log" element={<AdminAuditLog />} />
      {/* Phase 4 - Signage CMS */}
      <Route path="/admin/screens" element={<AdminScreens />} />
      <Route path="/admin/content" element={<AdminContent />} />
      <Route path="/admin/playlists" element={<AdminPlaylists />} />
      {/* Phase 5 - Revenue Modules */}
      <Route path="/admin/advertisers" element={<AdminAdvertisers />} />
      <Route path="/admin/campaigns" element={<AdminCampaigns />} />
      <Route path="/admin/billing" element={<AdminBilling />} />
      <Route path="/admin/billing/success" element={<AdminBilling />} />
      <Route path="/admin/billing/cancel" element={<AdminBilling />} />
      <Route path="/admin/revenue" element={<AdminRevenue />} />
      <Route path="/admin/settings" element={<AdminSettings />} />
      <Route path="/admin/entitlements" element={<AdminEntitlements />} />
      <Route path="/admin/entitlements-v2" element={<AdminEntitlementsV2 />} />
      <Route path="/admin/receipts" element={<AdminReceipts />} />
      <Route path="/admin/system-status" element={<AdminSystemStatus />} />
      <Route path="/admin/freeflow" element={<AdminFreeflow />} />
      <Route path="/admin/exit-auths" element={<AdminExitAuths />} />
      <Route path="/admin/barrier" element={<AdminBarrierControl />} />
      <Route path="/admin/barrier-control" element={<AdminBarrierControl />} />
      <Route path="/admin/cameras" element={<AdminCameras />} />
      <Route path="/admin/cctv" element={<AdminCCTV />} />
      <Route path="/admin/sessions" element={<AdminLiveSessions />} />
      <Route path="/admin/spaces" element={<AdminSpaceManagement />} />
      <Route path="/admin/reports/daily-movement" element={<AdminDailyMovement />} />
      <Route path="/admin/daily-movement" element={<AdminDailyMovement />} />
      <Route path="/admin/vehicle-history" element={<AdminVehicleHistory />} />
      {/* Stripe Terminal Management - Lazy loaded */}
      <Route path="/admin/kiosks" element={<Suspense fallback={<div>Loading...</div>}><AdminKiosks /></Suspense>} />
      <Route path="/admin/readers" element={<Suspense fallback={<div>Loading...</div>}><AdminReaders /></Suspense>} />
      {/* Kyle Rise Payments & Passes (Legacy - Phase 2) */}
      <Route path="/admin/kyle-rise" element={<KyleRiseDashboard />} />
      <Route path="/admin/kyle-rise/retailers" element={<KyleRiseRetailers />} />
      <Route path="/admin/kyle-rise/retailers/:retailerId" element={<KyleRiseRetailerDetail />} />
      <Route path="/admin/kyle-rise/staff-passes" element={<KyleRiseStaffPasses />} />
      <Route path="/admin/kyle-rise/staff-passes/:passId" element={<KyleRiseStaffPasses />} />
      <Route path="/admin/kyle-rise/resident-passes" element={<KyleRiseResidentPasses />} />
      <Route path="/admin/kyle-rise/resident-passes/:passId" element={<KyleRiseResidentPasses />} />
      <Route path="/admin/kyle-rise/payments" element={<KyleRisePayments />} />
      <Route path="/admin/kyle-rise/payments/:paymentId" element={<KyleRisePayments />} />
      <Route path="/admin/kyle-rise/interests" element={<KyleRiseInterests />} />
      <Route path="/admin/kyle-rise/interests/:interestId" element={<KyleRiseInterests />} />
      <Route path="/admin/kyle-rise/email-logs" element={<KyleRiseEmailLogs />} />
      <Route path="/admin/kyle-rise/email-logs/:emailId" element={<KyleRiseEmailLogs />} />
      <Route path="/admin/kyle-rise/qr-manager" element={<KyleRiseQRManager />} />
      <Route path="/admin/kyle-rise/reports" element={<KyleRiseReports />} />
      {/* Commercial Management Suite (Phase 3A - Multi-site) */}
      <Route path="/admin/commercial/sites" element={<CommercialSites />} />
      <Route path="/admin/commercial/payments" element={<CommercialPayments />} />
      <Route path="/admin/commercial/payments/:paymentId" element={<CommercialPaymentDetail />} />
      <Route path="/admin/commercial/qr-centre" element={<CommercialQRCentre />} />
      <Route path="/admin/commercial/retailers" element={<CommercialRetailers />} />
      <Route path="/admin/commercial/retailers/:retailerId" element={<CommercialRetailerDetail />} />
      <Route path="/admin/commercial/resident-passes" element={<CommercialResidentPasses />} />
      <Route path="/admin/commercial/resident-passes/:passId" element={<CommercialResidentPasses />} />
      <Route path="/admin/commercial/staff-passes" element={<CommercialStaffPasses />} />
      <Route path="/admin/commercial/staff-passes/:passId" element={<CommercialStaffPasses />} />
      <Route path="/admin/commercial/season-tickets" element={<CommercialSeasonTickets />} />
      <Route path="/admin/commercial/long-term" element={<CommercialLongTerm />} />
      <Route path="/admin/commercial/interests" element={<CommercialInterests />} />
      <Route path="/admin/commercial/interests/:interestId" element={<CommercialInterests />} />
      <Route path="/admin/commercial/finance" element={<CommercialFinance />} />
      <Route path="/admin/commercial/reports" element={<CommercialReports />} />
      <Route path="/admin/commercial/email-centre" element={<CommercialEmailCentre />} />
      <Route path="/admin/commercial/email-centre/:emailId" element={<CommercialEmailCentre />} />
      <Route path="/admin/commercial/audit" element={<CommercialAuditCentre />} />
      <Route path="/admin/commercial/settings" element={<CommercialSettings />} />
      <Route path="/admin/commercial/products-tariffs" element={<CommercialProductsTariffs />} />
      <Route path="/admin/test" element={<TestControlPanel />} />
      <Route path="/admin/test-panel" element={<TestControlPanel />} />
      {/* Advertising Management Module */}
      <Route path="/admin/advertising" element={<AdvertisingDashboard />} />
      <Route path="/admin/advertising/advertisers" element={<AdvertisingAdvertisers />} />
      <Route path="/admin/advertising/screens" element={<AdvertisingScreens />} />
      <Route path="/admin/advertising/campaigns" element={<AdvertisingCampaigns />} />
      <Route path="/admin/advertising/calendar" element={<AdvertisingCalendar />} />
      <Route path="/admin/advertising/approvals" element={<AdvertisingApprovals />} />
      <Route path="/admin/advertising/proof-of-play" element={<AdvertisingProofOfPlay />} />
      <Route path="/admin/advertising/billing" element={<AdvertisingBilling />} />
      {/* Advertiser Self-Service Portal */}
      <Route path="/advertiser/login" element={<AdvertiserLogin />} />
      <Route path="/advertiser/magic-login" element={<AdvertiserLogin />} />
      <Route path="/advertiser" element={<AdvertiserDashboard />} />
      <Route path="/advertiser/campaigns" element={<AdvertiserCampaigns />} />
      <Route path="/advertiser/campaigns/new" element={<CreateCampaign />} />
      <Route path="/advertiser/campaigns/:campaignId" element={<CampaignDetail />} />
      <Route path="/advertiser/billing" element={<AdvertiserBilling />} />
      <Route path="/advertiser/billing/success" element={<AdvertiserBilling />} />
      
      {/* Visit Routes (Mobile QR Scan) */}
      <Route path="/visit/:qrCodeId" element={<VisitCapture />} />
      <Route path="/visit/success" element={<VisitSuccess />} />
      
      {/* Screen Player (Signage Display) */}
      <Route path="/player/:screenId" element={<ScreenPlayer />} />
      
      {/* Mobile Payment (Wall QR) */}
      <Route path="/pay" element={<MobilePay />} />
      
      {/* Mobile Operations - Barrier Control Only */}
      <Route path="/mobile/control" element={<MobileOpsControl />} />
      
      {/* Radisson Benefit QR Routes (R9.94) */}
      <Route path="/radisson/restaurant" element={<RadissonRestaurant />} />
      <Route path="/radisson/spa" element={<RadissonSpa />} />
      <Route path="/radisson/pay" element={<RadissonPay />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AppRouter />
        <Toaster position="top-right" />
      </BrowserRouter>
    </div>
  );
}

export default App;
